"use client";

import React, { useState } from "react";
import { Expand } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const galleryImages = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_dog_luna.png",
  alt: "Luna, 3yrs - Golden Retriever",
  aspect: "aspect-[3/4]"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_cat_oliver.png",
  alt: "Oliver, 5yrs - Tabby Cat",
  aspect: "aspect-square"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_rabbit_clover.png",
  alt: "Clover, 2yrs - Holland Lop",
  aspect: "aspect-video"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_dog_max.png",
  alt: "Max, 4yrs - Beagle",
  aspect: "aspect-[4/5]"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_kitten_milo.png",
  alt: "Milo, 1yr - Siamese Mix",
  aspect: "aspect-[3/2]"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_dog_bella.png",
  alt: "Bella, 6yrs - Border Collie",
  aspect: "aspect-square"
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_hamster_peanut.png",
  alt: "Peanut, 1yr - Syrian Hamster",
  aspect: "aspect-[3/4]"
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_dog_charlie.png",
  alt: "Charlie, 2yrs - French Bulldog",
  aspect: "aspect-video"
}, {
  id: 9,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_cat_sophie.png",
  alt: "Sophie, 8yrs - Maine Coon",
  aspect: "aspect-[4/3]"
}, {
  id: 10,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687478/img/happy_puppy_cooper.png",
  alt: "Cooper, 6mos - Labradoodle",
  aspect: "aspect-[2/3]"
}];
function GalleryItem({
  image,
  index,
  onClick
}: {
  image: (typeof galleryImages)[0];
  index: number;
  onClick: (src: string, alt: string) => void;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    rootMargin: "50px"
  });
  return <div ref={ref} data-index={index} className={`break-inside-avoid mb-6 relative group cursor-pointer overflow-hidden rounded-4xl shadow-md transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} onClick={() => onClick(image.src, image.alt)}>
      <div className={`relative w-full ${image.aspect}`}>
        <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
        {}
        <div className="absolute inset-0 bg-primary/5 mix-blend-multiply pointer-events-none transition-opacity duration-500 group-hover:opacity-0" />
        
        {}
        <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="bg-background/95 text-primary px-4 py-2 rounded-lg shadow-lg transform scale-75 group-hover:scale-100 transition-transform duration-300 flex items-center gap-2">
            <span className="font-default font-semibold text-sm">{image.alt}</span>
            <Expand className="w-4 h-4" />
          </div>
        </div>
      </div>
    </div>;
}
export default function OurHappyPatients() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  const handleImageClick = (src: string, alt: string) => {
    setSelectedImage({
      src,
      alt
    });
    setLightboxOpen(true);
  };
  return <section data-section-id="222072" id="our-happy-patients" className="py-24 px-4 md:px-8 bg-background text-foreground relative overflow-hidden">
      {}
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <h2 className="font-default text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            Our Happy Patients
          </h2>
          <p className="text-muted-foreground font-default text-lg leading-relaxed">
            Meet some of the wonderful companions we've had the pleasure of caring for. From routine checkups to life-saving surgeries, their health is our greatest reward.
          </p>
        </div>

        {}
        <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-6">
          {galleryImages.map((image, i) => <GalleryItem key={image.id} index={i} image={image} onClick={handleImageClick} />)}
        </div>

        {}
        <div className="mt-20 flex justify-center">
          <Link to="/ServicesPage">
            <Button variant="outline" className="w-32 h-32 rounded-lg flex flex-col items-center justify-center border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-500 shadow-md hover:shadow-xl group">
              <span className="font-default text-xl font-semibold leading-none mb-1 group-hover:-translate-y-1 transition-transform duration-300">
                View
              </span>
              <span className="font-default text-xl font-semibold leading-none group-hover:translate-y-1 transition-transform duration-300">
                More
              </span>
            </Button>
          </Link>
        </div>
      </div>

      {}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] md:max-w-screen-xl bg-background/80 backdrop-blur-xl border-none p-0 flex items-center justify-center" showCloseButton={true}>
          <DialogTitle className="sr-only">Patient Gallery View</DialogTitle>
          <DialogDescription className="sr-only">
            {selectedImage?.alt || "Enlarged patient photo"}
          </DialogDescription>
          
          {selectedImage && <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12">
              <img src={selectedImage.src} alt={selectedImage.alt} className="max-w-full max-h-[85vh] object-contain rounded-4xl shadow-4xl" />
              <p className="absolute bottom-0 left-0 right-0 text-center text-foreground font-default font-medium text-lg pb-4 md:pb-8 drop-shadow-sm">
                {selectedImage.alt}
              </p>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}